// use-update-session-access.ts — Sessions-list suite, action.
//
// Changes a session's access (e.g. Public ↔ Private). Returns the updated session.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Access } from '@cabal/shared/domain/session/enums.js';
import { type Session } from '@cabal/shared/domain/session/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { sessionsListKeys } from './keys';

const SESSION_DETAIL_KEY = {
  Session: 'session',
} as const;

export interface UpdateSessionAccessVars {
  sessionId: string;
  access: Access;
}

export function useUpdateSessionAccess(): ActionResult<UpdateSessionAccessVars, Session> {
  const api = useApi();
  const apiSessions = api.sessions;
  const queryClient = useQueryClient();

  return useActionHook<UpdateSessionAccessVars, Session>(
    useMemo(
      () => ({
        mutationFn: async ({ sessionId, access }) => {
          const session = await apiSessions.byId(sessionId).update({ body: { access } });
          if (!session) {
            throw new Error('Session access update returned no session.');
          }
          return session;
        },
        onInvalidate: (_data, { sessionId }) => {
          queryClient.invalidateQueries({ queryKey: sessionsListKeys.all });
          // Phase 5 owns the session-detail key; until then mirror today's invalidation.
          queryClient.invalidateQueries({ queryKey: [SESSION_DETAIL_KEY.Session, sessionId] });
        },
      }),
      [apiSessions, queryClient],
    ),
  );
}
